/**
 * OnboardingChecklistWidget — getting-started checklist for the school owner
 * dashboard. Pulls live counts from /api/onboarding/status and renders a
 * progress bar + per-step CTA. Auto-hides when the user has dismissed it or
 * once every step is complete.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ArrowRight, Sparkles, X } from "lucide-react";

export default function OnboardingChecklistWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/onboarding/status");
      setData(res.data);
    } catch {
      setHidden(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function dismiss() {
    setHidden(true);
    try { await api.post("/onboarding/dismiss"); } catch { /* best-effort */ }
  }

  if (hidden || loading || !data) return null;
  if (data.dismissed || data.all_done) return null;

  const pct = Math.round((data.progress || 0) * 100);

  return (
    <section
      className="rounded-3xl border border-[#2563EB]/15 shadow-floating overflow-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#FEF3C7]"
      data-testid="dash-onboarding-checklist"
    >
      <header className="px-5 sm:px-6 pt-5 pb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] flex items-center justify-center shadow-floating shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold text-foreground tracking-tight">Get SchoolBoss up and running</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              {data.done} of {data.total} setup steps complete · {pct}%
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600 hover:bg-white/60 rounded-full p-1.5 transition-colors"
          aria-label="Dismiss checklist"
          data-testid="dash-onboarding-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className="px-5 sm:px-6 pb-2">
        <div className="h-2 bg-white/70 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] transition-[width] duration-500"
            style={{ width: `${pct}%` }}
            data-testid="dash-onboarding-progress"
          />
        </div>
      </div>

      <ul className="px-2 sm:px-3 pt-3 pb-4 grid sm:grid-cols-2 gap-1.5">
        {(data.steps || []).map((step) => (
          <li key={step.key}>
            <Link
              to={step.link || "#"}
              className={`flex items-start gap-3 p-3 rounded-2xl transition-colors hover:bg-white/80 ${step.done ? "opacity-70" : ""}`}
              data-testid={`dash-onboarding-step-${step.key}`}
            >
              {step.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className={`font-semibold text-sm ${step.done ? "line-through text-slate-500" : "text-foreground"}`}>
                  {step.label}
                </div>
                {!step.done && step.hint && (
                  <div className="text-xs text-slate-500 mt-0.5 leading-snug">{step.hint}</div>
                )}
              </div>
              {!step.done && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1D4ED8] shrink-0 pt-0.5">
                  {step.cta || "Open"} <ArrowRight className="w-3 h-3" />
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>

      <footer className="px-5 sm:px-6 py-3 bg-white/60 border-t border-white">
        <p className="text-xs text-slate-500">
          Tip: complete these in order and your dashboard, tuition collection, and parent portal will light up automatically.
        </p>
      </footer>
    </section>
  );
}
